// Warehouse and Location types and mock data

export interface Warehouse {
  code: string;
  name: string;
  address: string;
  type: "None" | "Main" | "Satellite" | "Retail" | "Storage" | "Distribution";
  totalCapacity?: number;
  currentUtilization: number;
  active: boolean;
  defaultReceiveLocation?: string;
  defaultPickLocation?: string;
}

export type LocationStatus = "Active" | "Inactive" | "Blocked";

export interface Location {
  locationCode: string;
  warehouseCode: string;
  aisle?: string;
  rack?: string;
  shelf?: string;
  bin?: string;
  // Dynamic hierarchy data - maps level number to value
  hierarchyData?: { [level: number]: string };
  locationType: "Storage" | "Picking" | "Receiving" | "Staging" | "Shipping" | "Quarantine";
  capacity?: number;
  currentStock: number;
  utilizationPercent: number;
  pickingSequence?: number;
  active: boolean;
  status: LocationStatus;
  lastActivityDate?: string; // ISO date string
  notes?: string;
}

// Mock Warehouses (matching Stock Items data)
export const mockWarehouses: Warehouse[] = [
  {
    code: "WH-A",
    name: "Warehouse A",
    address: "Unit 5, Industrial Estate, Manchester, M1 2AB, United Kingdom",
    type: "Main",
    totalCapacity: 10000,
    currentUtilization: 67,
    active: true,
    defaultReceiveLocation: "A01-R01-S01-B01",
    defaultPickLocation: "A01-R01-S01-B01",
  },
  {
    code: "WH-B",
    name: "Warehouse B",
    address: "12 Commerce Road, Birmingham, B2 4QA, United Kingdom",
    type: "Main",
    totalCapacity: 8000,
    currentUtilization: 54,
    active: true,
    defaultReceiveLocation: "B01-R01-S01-B01",
    defaultPickLocation: "B01-R01-S01-B01",
  },
  {
    code: "WH-C",
    name: "Warehouse C",
    address: "45 Storage Lane, Leeds, LS1 3EX, United Kingdom",
    type: "Satellite",
    totalCapacity: 5000,
    currentUtilization: 42,
    active: true,
    defaultReceiveLocation: "C01-MAIN",
    defaultPickLocation: "C01-MAIN",
  },
  {
    code: "RS",
    name: "Retail Store",
    address: "78 High Street, London, SW1A 1AA, United Kingdom",
    type: "Retail",
    totalCapacity: 2000,
    currentUtilization: 78,
    active: true,
    defaultReceiveLocation: "RS-MAIN",
    defaultPickLocation: "RS-MAIN",
  },
  {
    code: "DC",
    name: "Distribution Center",
    address: "200 Logistics Park, Bristol, BS1 6NF, United Kingdom",
    type: "Distribution",
    totalCapacity: 15000,
    currentUtilization: 61,
    active: true,
    defaultReceiveLocation: "DC-RCV-01",
    defaultPickLocation: "DC-PCK-01",
  },
  {
    code: "SR",
    name: "Storage Room",
    address: "3 Back Office, Liverpool, L1 8JQ, United Kingdom",
    type: "Storage",
    totalCapacity: 500,
    currentUtilization: 35,
    active: true,
    defaultReceiveLocation: "SR-MAIN",
    defaultPickLocation: "SR-MAIN",
  },
];

// Helper function to calculate location stock from stock items
// This will be called after stock items are generated
export const updateLocationStockFromItems = (locations: Location[], stockItemsData: any[]) => {
  // Create a map of location code to stock items
  const locationStockMap = new Map<string, { itemCount: number; totalQuantity: number }>();
  
  stockItemsData.forEach(item => {
    if (item.locationCode) {
      const existing = locationStockMap.get(item.locationCode) || { itemCount: 0, totalQuantity: 0 };
      locationStockMap.set(item.locationCode, {
        itemCount: existing.itemCount + 1,
        totalQuantity: existing.totalQuantity + (item.physicalQuantity || 0)
      });
    }
  });
  
  // Update locations with actual stock data
  return locations.map(location => {
    const stockData = locationStockMap.get(location.locationCode);
    if (stockData) {
      // Calculate capacity: actual stock + 35% buffer (rounded up to nearest 50)
      const bufferMultiplier = 1.35;
      const rawCapacity = stockData.totalQuantity * bufferMultiplier;
      const capacity = Math.ceil(rawCapacity / 50) * 50; // Round up to nearest 50
      const utilizationPercent = Math.round((stockData.totalQuantity / capacity) * 100);
      
      return {
        ...location,
        capacity,
        currentStock: stockData.totalQuantity,
        utilizationPercent
      };
    } else {
      // Empty location - set reasonable capacity based on location type
      const baseCapacity = location.locationType === 'Storage' ? 200 : 
                          location.locationType === 'Picking' ? 150 :
                          location.locationType === 'Receiving' ? 300 :
                          location.locationType === 'Staging' ? 250 :
                          location.locationType === 'Shipping' ? 300 : 100;
      
      return {
        ...location,
        capacity: baseCapacity,
        currentStock: 0,
        utilizationPercent: 0
      };
    }
  });
};

// Generate mock locations (50-100 locations across warehouses)
const generateLocations = (): Location[] => {
  const locations: Location[] = [];
  const locationTypes: Location["locationType"][] = ["Storage", "Picking", "Receiving", "Staging", "Shipping", "Quarantine"];

  // Warehouse A - Full hierarchy (36 locations)
  for (let a = 1; a <= 3; a++) {
    for (let r = 1; r <= 2; r++) {
      for (let s = 1; s <= 3; s++) {
        for (let b = 1; b <= 2; b++) {
          const aisle = `A${String(a).padStart(2, '0')}`;
          const rack = `R${String(r).padStart(2, '0')}`;
          const shelf = `S${String(s).padStart(2, '0')}`;
          const bin = `B${String(b).padStart(2, '0')}`;
          const locationCode = `${aisle}-${rack}-${shelf}-${bin}`;
          
          const rand = Math.random();
          const randomStatus: LocationStatus = rand > 0.95 ? "Blocked" : rand > 0.90 ? "Inactive" : "Active";
          const daysAgo = Math.floor(Math.random() * 30); // 0-30 days ago
          const lastActivity = new Date();
          lastActivity.setDate(lastActivity.getDate() - daysAgo);
          
          locations.push({
            locationCode,
            warehouseCode: "WH-A",
            aisle,
            rack,
            shelf,
            bin,
            locationType: locationTypes[Math.floor(Math.random() * locationTypes.length)],
            capacity: 100, // Will be updated based on actual stock
            currentStock: 0, // Will be updated based on actual stock
            utilizationPercent: 0, // Will be updated based on actual stock
            pickingSequence: locations.length + 1,
            active: true,
            status: randomStatus,
            lastActivityDate: lastActivity.toISOString(),
            notes: randomStatus === "Blocked" ? "Maintenance required" : undefined,
          });
        }
      }
    }
  }

  // Warehouse B - Aisle-Rack-Bin hierarchy (20 locations)
  for (let a = 1; a <= 4; a++) {
    for (let r = 1; r <= 5; r++) {
      const aisle = `B${String(a).padStart(2, '0')}`;
      const rack = `R${String(r).padStart(2, '0')}`;
      const bin = `B${String(r).padStart(2, '0')}`;
      const locationCode = `${aisle}-${rack}-${bin}`;
      
      const rand = Math.random();
      const randomStatus: LocationStatus = rand > 0.95 ? "Blocked" : rand > 0.90 ? "Inactive" : "Active";
      const daysAgo = Math.floor(Math.random() * 30);
      const lastActivity = new Date();
      lastActivity.setDate(lastActivity.getDate() - daysAgo);
      
      locations.push({
        locationCode,
        warehouseCode: "WH-B",
        aisle,
        rack,
        bin,
        locationType: locationTypes[Math.floor(Math.random() * locationTypes.length)],
        capacity: 100, // Will be updated based on actual stock
        currentStock: 0, // Will be updated based on actual stock
        utilizationPercent: 0, // Will be updated based on actual stock
        pickingSequence: locations.length + 1,
        active: true,
        status: randomStatus,
        lastActivityDate: lastActivity.toISOString(),
        notes: randomStatus === "Blocked" ? "Maintenance required" : undefined,
      });
    }
  }

  // Warehouse C - Simple aisle locations (10 locations)
  for (let a = 1; a <= 10; a++) {
    const aisle = `C${String(a).padStart(2, '0')}`;
    const locationCode = `${aisle}-MAIN`;
    
    const randomStatus: LocationStatus = Math.random() > 0.9 ? "Blocked" : "Active";
    const daysAgo = Math.floor(Math.random() * 30);
    const lastActivity = new Date();
    lastActivity.setDate(lastActivity.getDate() - daysAgo);
    
    locations.push({
      locationCode,
      warehouseCode: "WH-C",
      aisle,
      locationType: locationTypes[Math.floor(Math.random() * locationTypes.length)],
      capacity: 100, // Will be updated based on actual stock
      currentStock: 0, // Will be updated based on actual stock
      utilizationPercent: 0, // Will be updated based on actual stock
      pickingSequence: locations.length + 1,
      active: true,
      status: randomStatus,
      lastActivityDate: lastActivity.toISOString(),
      notes: randomStatus === "Blocked" ? "Maintenance required" : undefined,
    });
  }

  // Retail Store - Simple locations (8 locations)
  const retailAreas = ["MAIN", "BACK", "DISPLAY", "COUNTER", "STOCK-A", "STOCK-B", "RETURNS", "HOLD"];
  retailAreas.forEach((area, index) => {
    const locationCode = `RS-${area}`;
    
    const randomStatus: LocationStatus = Math.random() > 0.9 ? "Blocked" : "Active";
    const daysAgo = Math.floor(Math.random() * 30);
    const lastActivity = new Date();
    lastActivity.setDate(lastActivity.getDate() - daysAgo);
    
    locations.push({
      locationCode,
      warehouseCode: "RS",
      locationType: area.includes("STOCK") ? "Storage" : area === "RETURNS" ? "Quarantine" : "Picking",
      capacity: 100, // Will be updated based on actual stock
      currentStock: 0, // Will be updated based on actual stock
      utilizationPercent: 0, // Will be updated based on actual stock
      pickingSequence: index + 1,
      active: true,
      status: randomStatus,
      lastActivityDate: lastActivity.toISOString(),
      notes: randomStatus === "Blocked" ? "Maintenance required" : undefined,
    });
  });

  // Distribution Center - Specialized zones (15 locations)
  const dcZones = [
    { prefix: "RCV", count: 3, type: "Receiving" as const },
    { prefix: "STG", count: 3, type: "Staging" as const },
    { prefix: "PCK", count: 5, type: "Picking" as const },
    { prefix: "SHP", count: 3, type: "Shipping" as const },
    { prefix: "QTN", count: 1, type: "Quarantine" as const },
  ];

  dcZones.forEach(zone => {
    for (let i = 1; i <= zone.count; i++) {
      const locationCode = `DC-${zone.prefix}-${String(i).padStart(2, '0')}`;
      
      const rand = Math.random();
      const randomStatus: LocationStatus = rand > 0.95 ? "Blocked" : rand > 0.90 ? "Inactive" : "Active";
      const daysAgo = Math.floor(Math.random() * 30);
      const lastActivity = new Date();
      lastActivity.setDate(lastActivity.getDate() - daysAgo);
      
      locations.push({
        locationCode,
        warehouseCode: "DC",
        locationType: zone.type,
        capacity: 100, // Will be updated based on actual stock
        currentStock: 0, // Will be updated based on actual stock
        utilizationPercent: 0, // Will be updated based on actual stock
        pickingSequence: locations.length + 1,
        active: true,
        status: randomStatus,
        lastActivityDate: lastActivity.toISOString(),
        notes: randomStatus === "Blocked" ? "Maintenance required" : undefined,
      });
    }
  });

  // Storage Room - Simple locations (5 locations)
  for (let i = 1; i <= 5; i++) {
    const locationCode = `SR-${String(i).padStart(2, '0')}`;
    
    const randomStatus: LocationStatus = Math.random() > 0.9 ? "Blocked" : "Active";
    const daysAgo = Math.floor(Math.random() * 30);
    const lastActivity = new Date();
    lastActivity.setDate(lastActivity.getDate() - daysAgo);
    
    locations.push({
      locationCode,
      warehouseCode: "SR",
      locationType: "Storage",
      capacity: 100, // Will be updated based on actual stock
      currentStock: 0, // Will be updated based on actual stock
      utilizationPercent: 0, // Will be updated based on actual stock
      pickingSequence: i,
      active: true,
      status: randomStatus,
      lastActivityDate: lastActivity.toISOString(),
      notes: randomStatus === "Blocked" ? "Maintenance required" : undefined,
    });
  }

  return locations;
};

// Initial locations (will be updated with actual stock data)
const initialLocations = generateLocations();

// Export the initial locations (they will be updated when stock items are loaded)
export const mockLocations = initialLocations;
